<template>
  <div class="countdown-timer">
    <div class="timer-container">
      <h3 class="timer-title white--text mb-4">Time Remaining</h3>
      <div class="timer-display">
        <div class="time-unit">
          <div class="time-value">{{ days }}</div>
          <div class="time-label">Days</div>
        </div>
        <div class="time-separator">:</div>
        <div class="time-unit">
          <div class="time-value">{{ hours }}</div>
          <div class="time-label">Hours</div>
        </div>
        <div class="time-separator">:</div>
        <div class="time-unit">
          <div class="time-value">{{ minutes }}</div>
          <div class="time-label">Minutes</div>
        </div>
        <div class="time-separator">:</div>
        <div class="time-unit">
          <div class="time-value">{{ seconds }}</div>
          <div class="time-label">Seconds</div>
        </div>
      </div>
      <div v-if="isExpired" class="expired-message">
        <v-chip color="red" text-color="white" large>
          <v-icon left>mdi-clock-alert</v-icon>
          Sweepstakes Ended
        </v-chip>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CountdownTimer',
  props: {
    endTime: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      currentTime: new Date(),
      timer: null
    }
  },
  computed: {
    timeRemaining() {
      const end = new Date(this.endTime)
      const now = this.currentTime
      const diff = end.getTime() - now.getTime()
      
      if (diff <= 0) {
        return {
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0,
          total: 0
        }
      }
      
      return {
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((diff % (1000 * 60)) / 1000),
        total: diff
      }
    },
    
    days() {
      return this.formatTime(this.timeRemaining.days)
    },
    
    hours() {
      return this.formatTime(this.timeRemaining.hours)
    },
    
    minutes() {
      return this.formatTime(this.timeRemaining.minutes)
    },
    
    seconds() {
      return this.formatTime(this.timeRemaining.seconds)
    },
    
    isExpired() {
      return this.timeRemaining.total <= 0
    }
  },
  
  mounted() {
    this.startTimer()
  },
  
  beforeDestroy() {
    this.stopTimer()
  },
  
  methods: {
    startTimer() {
      this.timer = setInterval(() => {
        this.currentTime = new Date()
        
        if (this.isExpired) {
          this.stopTimer()
          this.$emit('expired')
        }
      }, 1000)
    },
    
    stopTimer() {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    },
    
    formatTime(value) {
      return value.toString().padStart(2, '0')
    }
  }
}
</script>

<style scoped>
.countdown-timer {
  text-align: center;
  width: 100%;
  max-width: 500px;
  margin: 0 auto;
}

.timer-container {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 24px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  width: 100%;
  box-sizing: border-box;
}

.timer-title {
  font-size: 1.5rem;
  font-weight: bold;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
}

.timer-display {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  flex-wrap: nowrap;
  max-width: 100%;
  overflow-x: auto;
}

.time-unit {
  background: rgba(255, 255, 255, 0.9);
  border-radius: 12px;
  padding: 16px 8px;
  min-width: 70px;
  flex: 1;
  max-width: 90px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.time-value {
  font-size: 2.2rem;
  font-weight: bold;
  color: #333;
  line-height: 1;
  white-space: nowrap;
}

.time-label {
  font-size: 0.8rem;
  color: #666;
  margin-top: 4px;
  text-transform: uppercase;
  letter-spacing: 1px;
  white-space: nowrap;
}

.time-separator {
  font-size: 1.8rem;
  font-weight: bold;
  color: white;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
  flex-shrink: 0;
  width: 20px;
  text-align: center;
}

.expired-message {
  margin-top: 16px;
}

@media (max-width: 768px) {
  .timer-container {
    padding: 20px 16px;
  }

  .timer-display {
    gap: 8px;
  }

  .time-unit {
    min-width: 60px;
    max-width: 75px;
    padding: 12px 6px;
  }

  .time-value {
    font-size: 1.8rem;
  }

  .time-label {
    font-size: 0.7rem;
  }

  .time-separator {
    font-size: 1.4rem;
    width: 15px;
  }

  .timer-title {
    font-size: 1.3rem;
  }
}

@media (max-width: 600px) {
  .timer-display {
    gap: 6px;
  }

  .time-unit {
    min-width: 55px;
    max-width: 65px;
    padding: 10px 4px;
  }

  .time-value {
    font-size: 1.6rem;
  }

  .time-separator {
    font-size: 1.2rem;
    width: 12px;
  }
}

@media (max-width: 480px) {
  .timer-container {
    padding: 16px 12px;
  }

  .timer-display {
    gap: 4px;
  }

  .time-unit {
    min-width: 50px;
    max-width: 60px;
    padding: 8px 3px;
  }

  .time-value {
    font-size: 1.4rem;
  }

  .time-label {
    font-size: 0.6rem;
  }

  .time-separator {
    font-size: 1rem;
    width: 10px;
  }

  .timer-title {
    font-size: 1.2rem;
    margin-bottom: 16px !important;
  }
}

@media (max-width: 360px) {
  .timer-display {
    flex-direction: column;
    gap: 8px;
  }

  .time-separator {
    display: none;
  }

  .time-unit {
    min-width: 80px;
    max-width: 100px;
    padding: 12px 8px;
  }

  .time-value {
    font-size: 1.8rem;
  }

  .time-label {
    font-size: 0.8rem;
  }
}
</style>
